import { List, Map as ImmutableMap, Record, Set as ImmutableSet } from 'immutable';
import type { RecordOf } from 'immutable';

import type { QcsSyncResourceStoreProps } from 'companion-app-components/flux/core/resourceStoreTypes';

import ExtendableError from 'es6-error';

export type InstitutionLoginAggregatorProps = {
  channel?: string | null;
  cpId?: string | null;
  aggStatus?: string | null;
  aggStatusCode?: string | null;
  aggStatusDetail?: string | null;
  lastStatusUpdatedAt?: string | null;
  lastRefreshSuccessfulAt?: string | null;
  lastRefreshAttemptedAt?: string | null;
};

export type InstitutionLoginAggregator = RecordOf<InstitutionLoginAggregatorProps>;

export const mkInstitutionLoginAggregator = Record<InstitutionLoginAggregatorProps>({
  channel: null,
  cpId: null,
  aggStatus: null,
  aggStatusCode: null,
  aggStatusDetail: null,
  lastStatusUpdatedAt: null,
  lastRefreshSuccessfulAt: null,
  lastRefreshAttemptedAt: null,
});

export type CredentialProps = {
  key?: string | null;
  value?: string | null;
};

export type Credential = RecordOf<CredentialProps>;

export const mkCredential = Record<CredentialProps>({
  key: 'unknown',
  value: 'unknown',
});

export type FiMigrationInfoProps = {
  toInstitutionId?: string | null;
  toChannel?: string | null;
};

export type FiMigrationInfo = RecordOf<FiMigrationInfoProps>;

export const mkFiMigrationInfo = Record<FiMigrationInfoProps>({
  toInstitutionId: 'unknown',
  toChannel: 'unknown',
});

export type InstitutionLoginProps = {
  id?: string | null;
  name?: string | null;
  institutionId?: string | null;
  brandingId?: string | null;
  cpInstitutionId?: string | null;
  ewcLive?: boolean;
  cpSetupMode?: null | 'DISCOVER_ACCOUNTS_ONLY' | 'DISCOVER_AND_ADD_ACCOUNTS' | 'DISCOVER_AND_REPLACE_ACCOUNTS' | 'SKIP_ACCOUNT_DISCOVERY' | 'UPDATE_CREDENTIALS';
  channel?: string | null;
  credentials?: List<Credential> | null;
  aggregators?: List<InstitutionLoginAggregator> | null;
  optionalAction?: string;
  fiMigrationInfo?: FiMigrationInfo;
  isDeleted?: boolean;
};

export type InstitutionLogin = RecordOf<InstitutionLoginProps>;

export const mkInstitutionLogin = Record<InstitutionLoginProps>({
  id: undefined,
  name: 'unknown',
  institutionId: undefined,
  brandingId: undefined,
  cpInstitutionId: undefined,
  ewcLive: false,
  cpSetupMode: undefined,
  channel: undefined,
  credentials: undefined,
  aggregators: undefined,
  optionalAction: undefined,
  fiMigrationInfo: undefined,
  isDeleted: false,
});

export type InstitutionLoginsStoreProps = QcsSyncResourceStoreProps<InstitutionLoginProps> & {
  isRefreshingAll: boolean;
  idsRefreshing: ImmutableSet<string>;
  idsRefreshingWithUserActionsPending: ImmutableSet<string>;
  idsRefreshingWithCancelledUserActions: ImmutableSet<string>;
  credentialBlobsForRefreshingLogins: ImmutableMap<string, string>;
  credentialsForRefreshingLogins: ImmutableMap<string, List<Credential>>;
};

export const mkInstitutionLoginsStore = Record<InstitutionLoginsStoreProps>({
  resourcesById: ImmutableMap(),
  lastSyncDate: null,
  loadPending: true,
  isLoading: false,
  isRefreshingAll: false,
  idsRefreshing: ImmutableSet(),
  idsRefreshingWithUserActionsPending: ImmutableSet(),
  idsRefreshingWithCancelledUserActions: ImmutableSet(),
  credentialBlobsForRefreshingLogins: ImmutableMap(),
  credentialsForRefreshingLogins: ImmutableMap(),
});

export type InstitutionLoginsStore = RecordOf<InstitutionLoginsStoreProps>;

export type GetInstitutionsPayload = {
  institutionLogins: Array<InstitutionLogin>;
  replace: boolean;
  loadPending: boolean;
  lastSyncDate: string;
};

// ================================================================================================
// MfaChallenge & MfaResponse
// ================================================================================================

export type MfaAnswerChoiceProps = {
  displayText: string;
};

export type MfaAnswerChoice = RecordOf<MfaAnswerChoiceProps>;

export const mkMfaAnswerChoice = Record<MfaAnswerChoiceProps>({
  displayText: 'unknown',
});

export type MfaChallengeItemProps = {
  type: string;

  key: string;
  sequence: number;

  base64Image?: string | null;
  question: string;
  answerChoices?: List<MfaAnswerChoice> | null;
};

export type MfaChallengeItem = RecordOf<MfaChallengeItemProps>;

export const mkMfaChallengeItem = Record<MfaChallengeItemProps>({
  type: 'QUESTION',
  key: '0',
  sequence: 0,
  base64Image: null,
  question: 'unknown',
  answerChoices: null,
});


export type MfaChallengeProps = {
  institutionLoginId: string;
  challenges: List<MfaChallengeItem>;
  submitUrl: string;
};

export type MfaChallenge = RecordOf<MfaChallengeProps>;

export const mkMfaChallenge = Record<MfaChallengeProps>({
  institutionLoginId: '0',
  challenges: List(),
  submitUrl: '',
});

export type MfaAnswerProps = {
  id: string;
  value: string;
};

export type MfaAnswer = RecordOf<MfaAnswerProps>;

export const mkMfaAnswer = Record<MfaAnswerProps>({
  id: '0',
  value: 'unknown',
});

type MfaResponseProps = {
  institutionLoginId: string;
  answers: List<MfaAnswer>;
  submitUrl: string;
};

export type MfaResponse = RecordOf<MfaResponseProps>;

export const mkMfaResponse = Record<MfaResponseProps>({
  institutionLoginId: '0',
  answers: List(),
  submitUrl: 'need_to_set_submit_url',
});

export class RefreshAccountsMfaFormSubmitError extends ExtendableError {
  institutionLoginId: string;

  constructor(message: string, institutionLoginId: string) {
    super(message);
    this.institutionLoginId = institutionLoginId;
  }
}

// ================================================================================================
// RefreshResponse -
// ================================================================================================

export type RefreshAccountsCompletedReason =
  'DONE_PROCESSING | NO_ACCOUNTS_TO_REFRESH | FAILED_WHEN_SUBMITTING | FAILED_WHEN_POLLING | TIMEOUT_WHILE_PROCESSING';

export type RefreshAggregatorProps = {
  channel?: string | null;

  isProcessing: boolean;
  aggStatus?: string | null;
  cpAggStatus?: string | null;
  cpAggStatusDetail?: string | null;

  mfaChallenges?: any;
  accounts?: any;
};

export type RefreshAggregator = RecordOf<RefreshAggregatorProps>;

export const makeRefreshAggregator = Record<RefreshAggregatorProps>({
  channel: undefined,
  isProcessing: false,
  aggStatus: undefined,
  cpAggStatus: undefined,
  cpAggStatusDetail: undefined,
  mfaChallenges: undefined,
  accounts: undefined,
});

export type RefreshInstitutionLoginProps = {
  id: string;
  isProcessing: boolean;
  status: string;
  credentialsBlob?: string | null;
  submitUrl?: string | null;
  aggregators?: List<RefreshAggregator> | null;
};

export type RefreshInstitutionLogin = RecordOf<RefreshInstitutionLoginProps>;

export const makeRefreshInstitutionLogin = Record<RefreshInstitutionLoginProps>({
  id: '0',
  isProcessing: false,
  status: 'OK',
  credentialsBlob: undefined,
  submitUrl: undefined,
  aggregators: undefined,
});

type RefreshResponseProps = {
  jobId: string;
  isProcessing: boolean;
  status: string;
  institutionLogins: List<RefreshInstitutionLogin>;
};

export type RefreshResponse = RecordOf<RefreshResponseProps>;

export const makeRefreshResponse = Record<RefreshResponseProps>({
  jobId: '0',
  isProcessing: false,
  status: 'OK',
  institutionLogins: List(),
});

export type LoginRefreshCredentialsType = {
  institutionLoginId: string;
  credentialsBlob?: string;
  credentials?: Array<Credential & { name: string }>;
};
